<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Message from '@uiKit/elements/Message.vue'
import TermsCheckbox from '@uiKit/components/TermsCheckbox.vue'
import { formatAed } from '@shared/utils'
import type { OrderFeeLine } from '@modules/orders'
import type { ReturnTile } from '../../property'

import InvestTokenSelector from './InvestTokenSelector.vue'
import InvestProjectedReturns from './InvestProjectedReturns.vue'
import InvestmentSummaryCard from './InvestmentSummaryCard.vue'
import PaymentMethodSelector, { type PaymentMethod } from './PaymentMethodSelector.vue'
import { orderValidationError, ownershipShareFor } from '../utils/tokenQuantityUtils'
import { buildOwnershipShareSheet, buildTokenPriceSheet } from '../constants/investInfo'

// Investment Details (Figma 32590:65397 / 66269) and Review Order (67141)
// share this body: token selector, projected returns, summary. Details adds
// the payment method, the terms tick and "Pay AED …"; Review swaps the footer
// for the Terms of Execution line and "Place Order".

export interface InvestQuote {
  tokenPrice: number
  minTokens: number
  maxTokens: number
  grossAmount: number
  totalFeeAmount: number
  netAmount: number
  fees: OrderFeeLine[]
}

interface Props {
  mode: 'details' | 'review'
  assetName: string
  totalTokens: number
  /** Sliceal price (purchase price) — the ownership-share base. */
  slicealPrice: number | null
  minInvestmentAmount: number | null
  maxInvestmentAmount: number | null
  tokensAvailable: number
  sliderMax: number
  quote: InvestQuote
  isQuoting: boolean
  feesError: string | null
  returnTiles: ReturnTile[]
  walletBalance: number
  orderError: string | null
  isInsufficientBalance: boolean
  submitting: boolean
}

const props = defineProps<Props>()

const tokens = defineModel<number>('tokens', { required: true })

const emit = defineEmits<{
  (e: 'continue'): void
  (e: 'place-order'): void
  (e: 'open-terms'): void
  (e: 'add-funds'): void
}>()

const paymentMethod = ref<PaymentMethod>('wallet')
const termsAccepted = ref(false)

const exceedsBalance = computed(() => props.quote.netAmount > props.walletBalance)
const ownershipShare = computed(() =>
  ownershipShareFor(props.quote.grossAmount, props.slicealPrice, tokens.value, props.totalTokens),
)
const ownershipSheet = computed(() =>
  buildOwnershipShareSheet({
    investmentValue: props.quote.grossAmount,
    slicealPrice: props.slicealPrice,
    ownershipShare: ownershipShare.value ?? '',
  }),
)
const tokenPriceSheet = computed(() =>
  buildTokenPriceSheet({
    tokenPrice: props.quote.tokenPrice,
    slicealPrice: props.slicealPrice,
    totalTokens: props.totalTokens,
  }),
)

// First blocking problem wins; any of them disables the pay / place button.
// An inactive wallet or blocked account never reaches this form — the
// eligibility blocker replaces it — so the wallet is treated as active here.
const validationError = computed(() =>
  orderValidationError({
    apiError: props.feesError,
    tokens: tokens.value,
    tokenPrice: props.quote.tokenPrice,
    minTokens: props.quote.minTokens,
    tokensAvailable: props.tokensAvailable,
    minInvestmentAmount: props.minInvestmentAmount,
    maxInvestmentAmount: props.maxInvestmentAmount,
    wallet:
      paymentMethod.value === 'wallet' ? { isActive: true, balance: props.walletBalance } : null,
    totalPayable: props.quote.netAmount,
  }),
)
const isOrderPayable = computed(
  () =>
    !validationError.value &&
    tokens.value > 0 &&
    paymentMethod.value === 'wallet' &&
    !props.isQuoting,
)
const canPay = computed(() => termsAccepted.value && isOrderPayable.value)
</script>

<template>
  <Box direction="col" grow class="order-form">
    <Box direction="col" gap="24px" class="order-form__body">
      <InvestTokenSelector
        v-model="tokens"
        :asset-name="assetName"
        :min-tokens="quote.minTokens"
        :max-tokens="quote.maxTokens"
        :slider-max="sliderMax"
        :investment-value="quote.grossAmount"
        :ownership-share="ownershipShare"
        :ownership-sheet="ownershipSheet"
      />

      <!-- Page-level problem with this order (quote error, stock, limits,
           wallet) — inline, so the investor can adjust and retry. -->
      <Message v-if="validationError" color="danger" variant="simple" size="small">
        <CurrencyText :text="validationError" />
      </Message>

      <InvestProjectedReturns :tiles="returnTiles" />

      <!-- No price, no summary: when the quote fails (e.g. max allocation
           reached) the error above explains why, and stale or blank totals
           would only mislead. -->
      <InvestmentSummaryCard
        v-if="!feesError"
        :token-price="quote.tokenPrice"
        :gross-amount="quote.grossAmount"
        :total-fee-amount="quote.totalFeeAmount"
        :net-amount="quote.netAmount"
        :fees="quote.fees"
        :is-quoting="isQuoting"
        :token-price-sheet="tokenPriceSheet"
      />

      <PaymentMethodSelector
        v-if="mode === 'details'"
        v-model="paymentMethod"
        :wallet-balance="walletBalance"
        :order-total="quote.netAmount"
        :exceeds-balance="exceedsBalance"
      />

      <Box v-if="orderError" direction="col" gap="2">
        <Message color="danger" variant="simple" size="small"
          ><CurrencyText :text="orderError"
        /></Message>
        <Button
          v-if="isInsufficientBalance"
          label="Add funds"
          icon="plus"
          variant="outline"
          size="small"
          block
          @click="emit('add-funds')"
        />
      </Box>
    </Box>

    <Box direction="col" gap="3" class="order-form__footer">
      <template v-if="mode === 'details'">
        <TermsCheckbox v-model="termsAccepted" size="lg">
          <Text type="body" weight="medium" color="var(--p-surface-600)" is="span"
            >I have read and agree to the&nbsp;<RouterLink
              to="#"
              class="order-form__link"
              @click.prevent="emit('open-terms')"
              >Investment Terms &amp; Conditions</RouterLink
            >&nbsp;and understand the risks involved.</Text
          >
        </TermsCheckbox>
        <Button
          :label="`Pay ${formatAed(quote.netAmount)}`"
          icon="arrow-right"
          icon-pos="right"
          size="large"
          block
          :disabled="!canPay"
          @click="emit('continue')"
        >
          <!-- Slot (not just :label) so the amount carries the currency mark; :label stays for the aria-label. -->
          <Text is="span" class="p-button-label"
            >Pay <CurrencyText :text="formatAed(quote.netAmount)"
          /></Text>
          <PiIcon name="arrow-right" class="p-button-icon p-button-icon-right" />
        </Button>
      </template>
      <template v-else>
        <Text type="body" color="var(--p-surface-600)" align="center">
          By ordering you agree to the&nbsp;<RouterLink
            to="#"
            class="order-form__link"
            @click.prevent="emit('open-terms')"
            >Terms of Execution</RouterLink
          >
        </Text>
        <Button
          label="Place Order"
          icon="arrow-right"
          icon-pos="right"
          size="large"
          block
          :loading="submitting"
          :disabled="!isOrderPayable || submitting"
          @click="emit('place-order')"
        />
      </template>
    </Box>
  </Box>
</template>

<style scoped>
.order-form {
  min-height: 100%;
  background: var(--p-surface-50);
}

.order-form__body {
  flex: 1;
  padding: 24px 16px;
}

.order-form__footer {
  position: sticky;
  bottom: 0;
  padding: 20px;
  border-top: 1px solid var(--p-surface-300);
  background: var(--p-surface-50);
}

.order-form__link {
  color: var(--p-primary-600);
  font-weight: 700;
  text-decoration: none;
}

.order-form__link:hover {
  text-decoration: underline;
}
</style>
